import { z } from "zod";
import type { CoachingConfirmationFact } from "@/lib/types";
/** Mirrors the bounded model while keeping the human's unsaved date input separate. */
export const confirmationFactSchema = z
  .object({
    statement: z
      .string()
      .trim()
      .min(1, "Enter your own factual statement.")
      .max(990),
    basis: z.enum(["observed", "reported"]),
    relationship: z.enum([
      "coach",
      "assistant_coach",
      "guardian",
      "self",
      "unknown",
    ]),
    observerId: z.string().nullable(),
    precision: z.enum(["instant", "day", "unknown"]),
    occurrence: z.string(),
  })
  .superRefine((value, context) => {
    if ((value.observerId === null) !== (value.relationship === "unknown"))
      context.addIssue({
        code: "custom",
        path: ["observerId"],
        message: "Select the actual observer or explicitly mark them unknown.",
      });
    if (
      value.basis === "observed" &&
      !["coach", "assistant_coach"].includes(value.relationship)
    )
      context.addIssue({
        code: "custom",
        path: ["relationship"],
        message:
          "Personally observed evidence needs your real coaching relationship.",
      });
    if (
      value.precision === "day" &&
      !z.iso.date().safeParse(value.occurrence).success
    )
      context.addIssue({
        code: "custom",
        path: ["occurrence"],
        message: "Enter the actual canonical day.",
      });
    if (
      value.precision === "instant" &&
      (!Number.isFinite(Date.parse(value.occurrence)) ||
        !/(Z|[+-]\d\d:\d\d)$/.test(value.occurrence))
    )
      context.addIssue({
        code: "custom",
        path: ["occurrence"],
        message: "Enter the actual instant with timezone.",
      });
  });
export type ConfirmationForm = z.infer<typeof confirmationFactSchema>;
export function humanConfirmationFact(
  form: ConfirmationForm,
): CoachingConfirmationFact {
  const value = confirmationFactSchema.parse(form);
  return {
    statement: value.statement,
    basis: value.basis,
    observer_relationship: value.relationship,
    observer_user_id: value.observerId,
    occurrence_precision: value.precision,
    occurred_at:
      value.precision === "unknown"
        ? null
        : value.precision === "day"
          ? value.occurrence
          : new Date(value.occurrence).toISOString(),
  };
}
